import Axios from "axios";
import { officeExtentions } from "../../../src/taskpane/config/constants";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { dataSourceItem, FilterItem, FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import authModule from "../../../utilities/AuthModule";
import { logger } from "../../../utilities/logger";
import { tryDecodeUriComponent, tryEncodeUriComponent } from "../../../utilities/EncodeDecode";
import { IAuthAccount } from "../../../src/taskpane/interfaces/IAuthTokenResultDto";
import { IViewsDropdown } from "../../../src/taskpane/interfaces/IViewsDropdown";
import { parseDocument } from "htmlparser2";
import { getElementsByTagName } from "domutils";
import { DatasourceHelper } from "../../../src/taskpane/components/helpers/dataSourceHelper";

const extractSelectOptions = (data: any) => {
    const options = getElementsByTagName("option", parseDocument(data));

    if (Array.isArray(options)) {
        return options.map((el) => {
            const option: FilterItem = {
                title: el.children[0]['data'],
                value: el.attribs.value,
                selected: false
            }
            return option;
        });
    }

    return [];
}

const extractListItems = (data: any) => {
    const list = getElementsByTagName("li", parseDocument(data)).splice(1);
    const options: FilterItem[] = [];

    if (Array.isArray(list)) {
        list.forEach((el, index) => {
            if (index % 5 === 0) {
                options.push({
                    title: el.children[0]['data'],
                    value: el.children[0]['data'],
                    selected: false
                })
            }
        });
    }

    return options;
}

export const resetFilterValues = (
    filterSections: FilterSection[]
): FilterSection[] => {
    return filterSections?.map(section => {
        return {
            ...section,
            showMoreExpanded: false,
            daysOffset: null,
            items: section.items.map(item => {
                return {
                    ...item,
                    selected: false
                }
            })
        }
    });
}

export const setFilterSectionsFromFields = async (
    isInOneDrive: boolean,
    oneDriveRootDetails: any,
    currentDirectory: IPageSettings,
    userAccount: IAuthAccount,
    filterFieldList: any[],
    hideTitleFieldFilter: boolean,
    serverRelPath: string,
    localizedMeText: string,
    selectedView?: IViewsDropdown,
    datasourceItems?: dataSourceItem[],
): Promise<FilterSection[]> => {
    try {
        const siteUrl = isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;
        const config = {
            headers: {
                Authorization: `Bearer ${isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken()}`,
            }
        }

        const filterSections = await Promise.all(
            filterFieldList
                ?.filter(x => x.RealFieldName !== 'FileSizeDisplay'
                    && x.RealFieldName !== '_UIVersionString'
                    && x.Filterable !== 'FALSE'
                    && x.ShowInFiltersPane !== 'FALSE'
                    && x.GroupField !== 'TRUE'
                    && !(hideTitleFieldFilter && x.RealFieldName === 'Title')
                )
                ?.map(async field => {
                    try {
                        const data = (await Axios.post(`${siteUrl}/_api/web/lists('${currentDirectory?.sharePointListId}')/RenderListFilterData?&FieldInternalName=${field.RealFieldName}&RootFolder=${tryEncodeUriComponent(tryDecodeUriComponent(serverRelPath))}&ExcludeFieldFilteringHtml=true${selectedView?.Id ? `&ViewId=${selectedView.Id}` : ""}`, {}, config)).data;

                        const section: FilterSection = {
                            filterField: field,
                            items: data.includes('<SELECT') ? extractSelectOptions(data) : extractListItems(data),
                            showMoreExpanded: false,
                            hasSlider: field.Type === 'DateTime' ? true : false,
                            showSlider: field.Type === 'DateTime' ? true : false
                        }

                        let compactItems: FilterItem[] = [];
                        section.items?.forEach(item => {
                            if (item.title !== '(All)' && item.title !== '(Alles)') {
                                if (field.RealFieldName === 'DocIcon') {
                                    if (officeExtentions.word.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'docx')) {
                                            compactItems.push({
                                                title: 'Word',
                                                value: officeExtentions.word.values,
                                                fileType: 'docx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.excel.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'xlsx')) {
                                            compactItems.push({
                                                title: 'Excel',
                                                value: officeExtentions.excel.values,
                                                fileType: 'xlsx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.powerPoint.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'pptx')) {
                                            compactItems.push({
                                                title: 'PowerPoint',
                                                value: officeExtentions.powerPoint.values,
                                                fileType: 'pptx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.visio.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'vsdx')) {
                                            compactItems.push({
                                                title: 'Visio',
                                                value: officeExtentions.visio.values,
                                                fileType: 'vsdx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (item.title !== `(Empty)` && item.title !== `(Leeg)`) {
                                        compactItems.push({ ...item, fileType: item.value });
                                    }
                                }
                                else if (field.FieldType === "User" || field.FieldType === "UserMulti") {
                                    if (item.title === userAccount.displayName) {
                                        compactItems.push({ ...item, title: `@${localizedMeText}` });
                                    }
                                    else {
                                        compactItems.push({ ...item });
                                    }
                                }
                                else {
                                    compactItems.push({ ...item });
                                }
                            }
                        });

                        // try {
                        //   const dataSourceField = DatasourceHelper.getInputDataSource(datasourceItems, field.DisplayName);
                        //   if (dataSourceField) {
                        //     const dropdownBinder = new dataSourceDropdownBinder(dataSourceService, dataSourceField, field.DisplayName);
                        //     const property = dropdownBinder.getDataSourcePropertyToBindTo();
                        //     if (property) {
                        //       const datasourceData = await dropdownBinder.getDatasourceEntries();
                        //       const datasourceValues = dropdownBinder.bindDataSourceToDropdown(datasourceData, property);

                        //       datasourceValues.forEach(datasourceValue => {
                        //         const datasourceFilterValue: string = (datasourceValue.key as string)?.trim();
                        //         if (datasourceFilterValue) {
                        //           const compactItem = compactItems.find(x => x.value?.trim().toUpperCase() === datasourceFilterValue.toUpperCase());
                        //           if (!compactItem) {
                        //             const newFilterItem: FilterItem = {
                        //               title: datasourceFilterValue,
                        //               value: datasourceFilterValue,
                        //               selected: false
                        //             }

                        //             compactItems.push(newFilterItem);
                        //           }
                        //         }
                        //       });
                        //     }
                        //   }
                        // } catch (error) {
                        //   logger(error, "CMFileFilterPanel.tsx (Add datasource options)");
                        // }

                        let useDefault: boolean = true;
                        if (field.FieldType === "DateTime") {
                            compactItems.sort((a, b) => {
                                try {
                                    // Convert the value of a and b to Date objects
                                    const aDate = new Date(a.value);
                                    const bDate = new Date(b.value);

                                    // Check if both a and b are valid dates
                                    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
                                        // If both are valid dates, compare their timestamps to sort them
                                        return aDate.getTime() - bDate.getTime();
                                    }
                                } catch (error) {
                                    // Ignore
                                }

                                // If either a or b (or both) are not valid dates, don't change their order
                                return 0;
                            });
                            useDefault = false;
                        }
                        else if (field.FieldType === "Number" && (field.RealFieldName === "Boekjaar" || field.DisplayName?.toLowerCase() === "boekjaar")) {
                            compactItems.sort((a, b) => {
                                const aNum = +a.value;
                                const bNum = +b.value;
                                if (isNaN(aNum) && isNaN(bNum)) return 0; // Both are bad, keep original order
                                if (isNaN(aNum)) return 1;                // A is bad, push it to the end
                                if (isNaN(bNum)) return -1;               // B is bad, push it to the end
                                // Both are good, sort in descending order
                                return bNum - aNum;
                            });
                            useDefault = false;
                        }
                        else if (field.FieldType === "Text" && compactItems.length > 0 && Array.isArray(datasourceItems)) {
                            const datasourceField = DatasourceHelper.getInputDataSource(datasourceItems, field.DisplayName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""]);
                            if (Array.isArray(datasourceField?.boundFields) && datasourceField.boundFields.length > 0) {
                                const boundedField = datasourceField.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === field.DisplayName.trim().toLowerCase()));
                                if (boundedField.multiSelect) {
                                    compactItems = compactItems.reduce(
                                        (accumulator: FilterItem[], originalItem: FilterItem) => {
                                            if (originalItem.value?.trim()) {
                                                const itemsToAdd: FilterItem[] = [];
                                                const valueParts: string[] = originalItem.value.split(",");
                                                if (valueParts.length > 1) {
                                                    itemsToAdd.push(
                                                        ...originalItem.value
                                                            .split(",")
                                                            .map(v => v.trim())
                                                            .filter(v => v)
                                                            .map(trimmedValue => ({
                                                                ...originalItem,
                                                                title: trimmedValue,
                                                                value: trimmedValue
                                                            }))
                                                    );
                                                }
                                                else {
                                                    itemsToAdd.push(originalItem);
                                                }
                                                return accumulator.concat(itemsToAdd);
                                            }

                                            return accumulator;
                                        },
                                        [] as FilterItem[]
                                    );
                                    section.datasourceMutated = true;
                                    useDefault = false;
                                }
                            }
                        }

                        if (useDefault) {
                            compactItems = compactItems
                                .sort((a, b) => a.title?.toLowerCase() > b.title?.toLowerCase() ? 1 : -1)
                                .map((item) => {
                                    return {
                                        ...item
                                    }
                                });
                        }

                        section.items = compactItems?.filter((obj, index, self) => index === self.findIndex((o) => o.title === obj.title));

                        return section;
                    } catch (error) {
                        logger(error, "FileFilterHelper.ts (filterFields)");
                        return null;
                    }
                }));

        let displayFilterSections: FilterSection[] = [];

        (filterSections as FilterSection[])
            ?.filter(section => section)
            ?.forEach(section => {
                if (!(section.filterField.RealFieldName === 'ID' && section.filterField.FromBaseType === 'TRUE')) {
                    if (section.items?.length === 1) {
                        // Hide section if only (Empty)
                        if (section.items[0].title !== "(Empty)" && section.items[0].title !== "(Leeg)") {
                            section.showSlider = false;
                            section.hasSlider = false;
                            displayFilterSections.push(section);
                        }
                    }
                    else if (section.items?.length === 2) {
                        // Hide section slider if only 1 item
                        if (section.items[0].title === "(Empty)" || section.items[0].title === "(Leeg)") {
                            section.showSlider = false;
                            section.hasSlider = false;
                        }
                        displayFilterSections.push(section);
                    }
                    else {
                        displayFilterSections.push(section);
                    }
                }
            });

        const forceToFrontFieldName: string = "ContentType";
        const forceToFrontIndex: number = displayFilterSections.findIndex(x => x?.filterField?.RealFieldName === forceToFrontFieldName);
        if (forceToFrontIndex !== -1) {
            displayFilterSections.unshift(displayFilterSections.splice(forceToFrontIndex, 1)[0]);
        }
        return displayFilterSections.filter(section => section?.items?.length > 1);
    } catch (error) {
        logger(error, "FileFilterHelper.ts (setFilterSectionsFromFields)");
    }

    return [];
}

export const escapeXml = (unsafe: string): string => {
    if (!unsafe) return '';
    return unsafe.replace(/[<>&'"]/g, function (c) {
        switch (c) {
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '&': return '&amp;';
            case '\'': return '&apos;';
            case '"': return '&quot;';
            default: return c; // Should not happen with the regex
        }
    });
}

/**
 * Recursively builds a linearly nested chain of <Or> or <And> clauses.
 * @param conditions An array of pre-formatted CAML condition strings.
 * @param operator The operator name ('Or' or 'And').
 * @returns A single CAML string representing the linearly nested structure, or an empty string if no conditions.
 */
export const buildLinearRecursiveCAML = (conditions: string[], operator: "Or" | "And"): string => {
    const count = conditions.length;
    if (count === 0) return "";
    if (count === 1) return conditions[0];

    const remainingConditions = conditions.slice(0, count - 1);
    const lastCondition = conditions[count - 1];
    const nestedStructure = buildLinearRecursiveCAML(remainingConditions, operator);

    if (!nestedStructure) return lastCondition; // Should only happen if remainingConditions had one item
    return `<${operator}>${nestedStructure}${lastCondition}</${operator}>`;
}